import {
  CreditCardBorrowerDocumentationRequired,
  DocumentationRequired,
  DocumentStatus,
  patchSendDocumentationIncidenceNotification,
} from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { DocumentStatusWithSent } from '@/components/modules/Documentation/types/DocumentStatusWithSent';
import { useRequest } from '@/hooks/requests/useRequest';

interface UseDocumentationRequired {
  documentationRequired: DocumentationRequired | CreditCardBorrowerDocumentationRequired;
  loanId: string;
  setOpened?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useDocumentationRequired = ({ documentationRequired, loanId, setOpened }: UseDocumentationRequired) => {
  const { request, loading } = useRequest();

  const hasIncidence: boolean = useMemo(() => {
    if (!documentationRequired.requiredFiles) return false;
    return documentationRequired.requiredFiles.some((requiredFile) => requiredFile.status === DocumentStatus.INCIDENCE);
  }, [documentationRequired]);

  const hasAllFilesSent: boolean = useMemo(() => {
    if (!documentationRequired.requiredFiles) return false;
    return !documentationRequired.requiredFiles.some((requiredFile) => !requiredFile.isSent);
  }, [documentationRequired]);

  const uploadedProgress: number = useMemo(() => {
    if (!documentationRequired.requiredFiles) return 0;
    const uploadedDocsCount = documentationRequired.requiredFiles.reduce(
      (a, b) => (b.status === DocumentStatus.UPLOADED ? a + 1 : a),
      0,
    );
    return (uploadedDocsCount / documentationRequired.requiredFiles.length) * 100;
  }, [documentationRequired]);

  const status: DocumentStatusWithSent = useMemo(() => {
    if (hasIncidence) return DocumentStatusWithSent.INCIDENCE;
    if (hasAllFilesSent) return DocumentStatusWithSent.SENT;
    if (documentationRequired?.areRequiredFilesUploaded && documentationRequired?.areDocumentsConfirmed)
      return DocumentStatusWithSent.UPLOADED;
    return DocumentStatusWithSent.PENDING;
  }, [documentationRequired]);

  const sendDocumentationIncidenceNotification = async () => {
    if (!('borrowerId' in documentationRequired) || !setOpened) return;
    await request(patchSendDocumentationIncidenceNotification, {
      id: loanId,
      borrowerId: documentationRequired.borrowerId,
    });
    setOpened(false);
  };

  return {
    status,
    hasAllFilesSent,
    hasIncidence,
    uploadedProgress,
    sendDocumentationIncidenceNotification,
    loading,
  };
};
